// src/components/ScrollToTop.tsx
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// The flight search results tabs (bundle / dynamic) are separate routes, so
// switching tabs is a pathname change. Navigating between any of these routes
// must not scroll (or move focus) — the user is on the same results page.
// Keep in sync with SearchResultsRoutes (SearchResults.tsx); listed locally so
// the app shell doesn't import the search-results component tree.
const FLIGHT_RESULTS_ROUTES = new Set<string>([
  '/flights',
  '/flights-async',
  '/flights/dynamic',
  '/flights/dynamic-async',
]);

/**
 * Smooth scroll-to-top (or scroll-to-hash) component.
 * Scrolls only when pathname or hash changes, not when search params change.
 * Skips scrolling when navigating between flight results routes (tab switch).
 * Respects prefers-reduced-motion and focuses main content after scrolling.
 */
const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = pathname;

    if (
      prevPathname &&
      prevPathname !== pathname &&
      FLIGHT_RESULTS_ROUTES.has(prevPathname) &&
      FLIGHT_RESULTS_ROUTES.has(pathname)
    ) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    // Allow DOM to render first
    requestAnimationFrame(() => {
      if (hash) {
        const el = document.querySelector(hash) as HTMLElement | null;

        if (el) {
          // Smooth (or auto) scroll to anchor
          el.scrollIntoView({ behavior, block: 'start' });
          // Focus after a short delay to avoid fighting the scrolling animation
          const focusDelay = behavior === 'smooth' ? 300 : 0;
          setTimeout(() => {
            try {
              el.focus?.({ preventScroll: true } as FocusOptions);
              if (document.activeElement !== el) {
                el.focus?.();
              }
            } catch {
              el.focus?.();
            }
          }, focusDelay);

          return;
        }
      }

      // Default: smooth scroll to top
      window.scrollTo({ top: 0, left: 0, behavior });

      // Focus the main area after (give a little time if we used smooth)
      const main = document.getElementById('main') || document.getElementById('App');
      if (main) {
        const focusDelay = behavior === 'smooth' ? 350 : 0;

        setTimeout(() => {
          try {
            main.focus?.({ preventScroll: true } as FocusOptions);
            if (document.activeElement !== main) {
              main.focus?.();
            }
          } catch {
            main.focus?.();
          }
        }, focusDelay);
      }
    });
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
